import { AlertTriangle, Check } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { getCombinedCheckoutByRequest } from "./api";
import { businessErrorMessage } from "./businessErrorMessage";
import {
  type CombinedCheckoutRecoveryView,
  combinedCheckoutRecoveryContext,
  readCombinedCheckoutRecovery,
  reconcileCombinedCheckoutAuthority,
} from "./combinedCheckoutRecovery";

export function CombinedCheckoutRecoveryProbe({
  operator,
  recoveryView,
  onRecoveryViewChange,
  onCompleted,
}: {
  operator: { tenantId: string; storeId: string; id: string };
  recoveryView: CombinedCheckoutRecoveryView;
  onRecoveryViewChange: (view: CombinedCheckoutRecoveryView) => void;
  onCompleted: (sessionIds: readonly string[]) => void;
}): React.JSX.Element | null {
  const context = useMemo(() => combinedCheckoutRecoveryContext(operator), [operator]);
  const readingRef = useRef<string | null>(null);
  const completedRef = useRef(onCompleted);
  completedRef.current = onCompleted;
  const [feedback, setFeedback] = useState<{
    kind: "SUCCESS" | "ERROR" | "UNKNOWN";
    message: string;
  } | null>(null);

  useEffect(() => {
    const current = readCombinedCheckoutRecovery(context);
    if (current.status !== recoveryView.status) onRecoveryViewChange(current);
    if (current.status === "NONE") {
      readingRef.current = null;
      return;
    }
    if (current.status !== "OWNED") {
      readingRef.current = null;
      setFeedback({
        kind: "ERROR",
        message:
          current.status === "CONTEXT_MISMATCH"
            ? "本机保存着其他员工的合并收款原命令。关联球台暂时不能清台或释放，请切回原账号核对。"
            : "浏览器无法安全读取合并收款保护记录。可能关联的球台暂时不能清台或释放。",
      });
      return;
    }
    if (current.recovery.state === "RETRY_ALLOWED") {
      readingRef.current = null;
      setFeedback({
        kind: "UNKNOWN",
        message:
          "门店权威记录已明确返回原合并收款没有执行。关联球台继续锁定；请回到结账页，只能使用保存的原请求号重试。",
      });
      return;
    }
    if (current.recovery.state !== "OUTCOME_UNKNOWN") {
      readingRef.current = null;
      return;
    }
    const requestKey = current.recovery.command.request.idempotencyKey;
    if (readingRef.current === requestKey) return;
    readingRef.current = requestKey;
    setFeedback({
      kind: "UNKNOWN",
      message: "正在用原请求号读取合并收款结果。确认前，关联球台不能清台或释放。",
    });
    void getCombinedCheckoutByRequest(
      requestKey,
      current.recovery.command.request,
      current.recovery.command.expected,
    )
      .then((result) => {
        const resolution = reconcileCombinedCheckoutAuthority(current.recovery, context, result);
        const next = readCombinedCheckoutRecovery(context);
        onRecoveryViewChange(next);
        if (resolution.status === "EXECUTED") {
          setFeedback({
            kind: "SUCCESS",
            message: `已从门店权威记录确认原合并收款完成，共 ${resolution.result.orders.length} 张台单；现在可以按正常步骤清台。`,
          });
          completedRef.current(resolution.result.orders.map((order) => order.sessionId));
        } else if (resolution.status === "RETRY_ALLOWED") {
          setFeedback({
            kind: "UNKNOWN",
            message:
              "门店权威记录已明确返回原合并收款没有执行。关联球台继续锁定；请回到结账页，只能使用保存的原请求号重试。",
          });
        } else {
          setFeedback({
            kind: "ERROR",
            message: "权威结果与本机原命令无法安全对应。关联球台继续锁定，不能清台或释放。",
          });
        }
      })
      .catch((error: unknown) => {
        setFeedback({
          kind: "ERROR",
          message: `原合并收款结果读取失败：${businessErrorMessage(error, "系统没有返回明确原因")}。关联球台继续锁定，不能清台或释放；请检查门店网络后刷新页面重新读取。`,
        });
      });
  }, [context, onRecoveryViewChange, recoveryView]);

  if (!feedback) return null;
  return (
    <section
      className={feedback.kind === "SUCCESS" ? "success-band" : "error-band"}
      role={feedback.kind === "SUCCESS" ? "status" : "alert"}
      aria-live="assertive"
    >
      {feedback.kind === "SUCCESS" ? (
        <Check size={18} aria-hidden="true" />
      ) : (
        <AlertTriangle size={19} aria-hidden="true" />
      )}
      <span>{feedback.message}</span>
    </section>
  );
}
